import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Card, Spinner } from "@/components/primitives";
import { Icon } from "@/components/Icon";
import { completeGoogleSignIn } from "@/lib/oauth";
import { restoreSession } from "@/lib/auth";

/** Landing page for the Cognito hosted-UI redirect after Google sign-in. */
export function OAuthCallback() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  // The authorization code is single-use; StrictMode's double effect would
  // spend it twice and fail a sign-in that actually succeeded.
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    const code = params.get("code");
    const oauthError = params.get("error_description") ?? params.get("error");
    if (oauthError) {
      setError(oauthError);
      return;
    }
    if (!code) {
      setError("This sign-in link is missing its authorization code.");
      return;
    }

    completeGoogleSignIn(code)
      // restoreSession() reads the tokens we just stored and populates auth state.
      .then(() => restoreSession())
      .then(() => navigate("/", { replace: true }))
      .catch((err: Error) => setError(err.message));
  }, [params, navigate]);

  return (
    <div style={{ display: "grid", placeItems: "center", padding: 48 }}>
      <Card style={{ width: 380, padding: 24 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 18,
          }}
        >
          <span
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: "var(--primary)",
              color: "var(--primary-foreground)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon name="pulse" size={18} />
          </span>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Status Watch</div>
        </div>

        {error ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div
              style={{
                fontSize: 12,
                color: "var(--destructive)",
                padding: 10,
                borderRadius: 8,
                background:
                  "color-mix(in oklch, var(--destructive) 10%, transparent)",
                lineHeight: 1.5,
              }}
            >
              {error}
            </div>
            <Button
              variant="primary"
              onClick={() => navigate("/signin", { replace: true })}
            >
              Back to sign in
            </Button>
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              fontSize: 13,
              color: "var(--muted-foreground)",
            }}
          >
            <Spinner />
            Completing sign-in…
          </div>
        )}
      </Card>
    </div>
  );
}
